import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../styles/SearchOverlay.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* Helper to resolve image path */
const resolveImageUrl = (path) => {
  if (!path) return "/no-image.png"; // optional fallback
  if (path.startsWith("http")) return path; // safety
  return `${API_BASE_URL}${path}`;
};

export default function SearchOverlay({ show, onClose }) {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [magazines, setMagazines] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Page loader

  const PageLoader = () => {
  return (
    <div
      style={{
        padding: "30px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <div
        className="spinner-border"
        role="status"
        style={{ width: "2.5rem", height: "2.5rem", color: "#f64a1aff" }}
      />
      <p className="mt-3 fw-semibold">Searching...</p>
    </div>
  );
};


  /* Prevent background scroll */
  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [show]);

  /* Search API */
  useEffect(() => {
    if (!show || searchTerm.trim() === "") {
      setMagazines([]);
      setBooks([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}search.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: searchTerm }),
        });

        const data = await res.json();

        if (data?.head?.code === 200) {
          setMagazines(data.body.magazines || []);
          setBooks(data.body.books || []);
        }
      } catch (err) {
        console.error("Search failed", err);
      } finally {
        setLoading(false);
        setHasSearched(true);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, show]);

  if (!show) return null;

  return (
    <motion.div
      className="search-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <div className="search-overlay-backdrop" onClick={onClose} />

      {/* Content */}
      <motion.div
        className="search-overlay-content"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35 }}
      >
        {/* Header */}
        <div className="search-header">
          <h5 className="fw-semibold mb-0">Search</h5>
          <button className="btn btn-dark btn-sm" onClick={onClose}>
            Close ✕
          </button>
        </div>

        {/* Search Input */}
        <input
          type="text"
          className="form-control mb-4"
          placeholder="Search magazines or books or Author Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />

        {loading && <PageLoader />}

        {!loading && hasSearched && searchTerm && (
          <>
            {/* Magazines */}
            {magazines.length > 0 && (
              <div className="mb-4">
                <h6>Magazines</h6>
                <div className="search-grid">
                  {magazines.map((mag) => (
                    <div
                      key={mag.magid}
                      className="search-item"
                      onClick={() => {
                        onClose();
                        navigate(
                          `/subscription/${mag.magid}?mp_id=${mag.mpid}&sp_id=${mag.spid}`
                        );
                      }}
                    >
                      <img
                        src={resolveImageUrl(mag.coverimg)}
                        alt={mag.magname}
                        loading="lazy"
                      />
                      <div className="search-title">{mag.magname}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Books */}
            {books.length > 0 && (
              <div className="mb-5">
                <h6>Books</h6>
                <div className="search-grid">
                  {books.map((book) => (
                    <div
                      key={book.pid || book.id}
                      className="search-item"
                      onClick={() => {
                        onClose();
                        navigate(
                          `/bookdescription/${book.pid || book.id}?mp_id=${
                            book.mpid
                          }&sp_id=${book.spid}`
                        );
                      }}
                    >
                      <img
                        src={resolveImageUrl(book.firstimg)}
                        alt={book.pname}
                        loading="lazy"
                      />
                      <div className="search-title">{book.pname}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!magazines.length && !books.length && (
              <p className="text-muted">No results found.</p>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
